import React from "react";

/**
 * Escape special regex characters in a string.
 */
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * React component that renders plain text with the search query highlighted.
 * Use for article titles (plain-text content).
 */
export function HighlightText({ text, query }) {
  if (!query || !text) return <>{text}</>;

  const trimmed = query.trim();
  if (!trimmed) return <>{text}</>;

  const regex = new RegExp(`(${escapeRegExp(trimmed)})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="search-highlight">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

/**
 * Insert <mark> tags around search matches inside an HTML string,
 * only touching text nodes (not tag names or attributes).
 * Use for article descriptions rendered via dangerouslySetInnerHTML.
 */
export function highlightHtml(html, query) {
  if (!query || !html) return html;

  const trimmed = query.trim();
  if (!trimmed) return html;

  const escaped = escapeRegExp(trimmed);
  const regex = new RegExp(escaped, "gi");

  let result = "";
  let i = 0;

  while (i < html.length) {
    if (html[i] === "<") {
      const closeIndex = html.indexOf(">", i);
      if (closeIndex === -1) {
        result += html.substring(i);
        break;
      }
      result += html.substring(i, closeIndex + 1);
      i = closeIndex + 1;
    } else if (html[i] === "&") {
      const semiIndex = html.indexOf(";", i);
      if (semiIndex !== -1 && semiIndex - i < 10) {
        result += html.substring(i, semiIndex + 1);
        i = semiIndex + 1;
      } else {
        result += html[i];
        i++;
      }
    } else {
      let textEnd = i;
      while (textEnd < html.length && html[textEnd] !== "<" && html[textEnd] !== "&") {
        textEnd++;
      }
      const textChunk = html.substring(i, textEnd);
      result += textChunk.replace(regex, '<mark class="search-highlight">$&</mark>');
      i = textEnd;
    }
  }

  return result;
}
